'use client';
import { useCallback, useEffect, useState } from 'react';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { generateLink } from '@/utils/formatters/generate-link';
import { removeNonNumericCharacters } from '@/utils/formatters/format-whatsapp';
import { scheduleDefaultWhatsappMessage } from '@/utils/consts/cta-whatsapp-messages.consts';
import { getLogger } from '@/logger/logger';

const formatMessage = (text: string, sessionId?: string) =>
  sessionId ? `${text}${text.endsWith('.') ? '' : '.'} Meu protocolo é ${sessionId}.` : text;

export const useWhatsappLinkWithAnalytics = (phone: string, text = scheduleDefaultWhatsappMessage) => {
  const analytics = getAnalyticsPlugin();
  const [url, setUrl] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');

  const resolveLink = useCallback(
    (phoneVariant: string) => {
      try {
        const finalText = formatMessage(text, sessionId);
        return generateLink({
          url: 'https://api.whatsapp.com/send',
          params: [
            { key: 'phone', value: `55${removeNonNumericCharacters(phoneVariant)}` },
            { key: 'text', value: finalText },
          ],
        });
      } catch {
        const logger = getLogger('useWhatsappLinkWithAnalytics');
        logger.error('Error generating WhatsApp link with analytics', { phoneVariant, text, sessionId });
        return '';
      }
    },
    [sessionId, text],
  );

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      const sessionId = await analytics.getCurrentSessionId();
      const finalText = formatMessage(text, sessionId);

      if (sessionId) {
        setSessionId(sessionId);
      }

      if (mounted) {
        setUrl(
          generateLink({
            url: 'https://api.whatsapp.com/send',
            params: [
              { key: 'phone', value: `55${removeNonNumericCharacters(phone)}` },
              { key: 'text', value: finalText },
            ],
          }),
        );
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [analytics, phone, text]);

  return { url, resolveLink };
};
